import { SelectedOwnerItem } from '../shared/components/owner-search/owner-search.component';
import { BookingDetails } from './bookings.model';
import { SortOrder } from './common.enums';
import { AvailabilityType, EventTime, RecurrenceRule, SingleDayEventData, SpaceView } from './find-a-space.model';
import { DashboardFeature, DashboardSorting, PointOfContactSearchCriteria, SpecialistSearchCriteria, User } from './dashboard-common.model';
import { EventType, IdWithRevision, Item, ItemWithDescription, SpaceSetupType, SpaceType, StatusReason } from './masterdata.model';
import { IDynamicTab } from '../common/dynamic-tabs/models/dynamic-tabs.model';
import { MultiSelectItem } from '@usitsdasdesign/dds-ng/multi-select';

export interface MeetingsDashboardInput {
  limit?: number;
  page: number;
  term?: string;
  buildingIds?: number[];
  statusTypes?: string[];
  classifications?: string[];
  pointOfContactSearchCriteria?: PointOfContactSearchCriteria;
  reservationSpecialistSearchCriteria?: SpecialistSearchCriteria;
  startDate: string;
  endDate: string;
  meetingDashboardSortings?: DashboardSorting[];
  features?: string;
  assignedToMe?: boolean;
  seriesIds?: number[];
  searchableFields?: string;
  isConsultationRequested?: boolean;
}

export interface MeetingDashboardResponse {
  items: MeetingDashboardItem[];
  totalCount: number;
}

export interface MeetingDashboardItem {
  buildingNames: string[];
  reservationId: number;
  reservationName: string;
  startDate: string;
  endDate: string;
  classification: string;
  createDate: string;
  locationCount: number;
  estimatedAttendeesCount: number;
  reservationSpecialist: User;
  pointOfContact: User;
  status: string;
  canCancel: boolean;
  canEdit: boolean;
  revision: number;
  isMultiBuilding: boolean;
  technologyType: string;
  bookingCount: number;
  vip: boolean;
  hasCatering: boolean;
  hasCateringContactMeLater: boolean;
  hasAttachments: boolean;
  hasTask: boolean;
  hasComments: boolean;
  hasCommentsAlert: boolean;
  consultationRequested: boolean;
}

export interface ReservationItemsInput {
  limit?: number;
  page?: number;
  itemTypes?: ReservationItemType[];
  buildingIds?: number[];
  spaceTypes?: string[];
  spaceBookingStatus?: string[];
  meetingWaitListStatus?: string[];
  bookingSpecialistSearchCriteria?: SpecialistSearchCriteria;
  features?: DashboardFeature | string;
  reservationItemSortings?: DashboardSorting[];
}

export interface ReservationItemsResponse {
  items: Array<ReservationItem>;
  totalCount: number;
  reservationItemFacets: ReservationItemFacets;
  itemCount: {
    meetingWaitList: ReservationItemCount;
    virtualBooking: ReservationItemCount;
    spaceBooking: ReservationItemCount;
  };
}

export interface ReservationItemFacets {
  spaceBookingSpecialists: User[];
  buildings: MultiSelectItem[];
  spaces: ReservationItemSpace[];
}

export interface ReservationItemSpace {
  id: number;
  name: string;
  buildingId: number;
  buildingDisplayName: string;
  floorUniqueKey: number;
  floorName: string;
  spaceType: SpaceType;
  capacity: number;
  centerCoordinates: { x: number; y: number; pointSize: number };
  sizeCoordinates: { top: number; left: number; height: number; width: number };
}

export interface ReservationItem {
  startDate: string;
  endDate: string;
  itemType: string;
  timeZoneAbbreviation: string;
  virtualBooking: ReservationItemVirtualBooking;
  spaceBooking: ReservationItemSpaceBooking;
  meetingWaitList: ReservationItemMeetingWaitList;
}

export interface ReservationItemVirtualBooking {
  virtualBookingTechnologyTypes: string;
  id: string;
  status: string;
  producer: User;
  facilitator: User;
  canEdit: boolean;
  canCancel: boolean;
  name: string;
}

export interface ReservationItemSpaceBooking extends Item {
  space: ReservationItemSpace;
  specialist: User;
  status: string;
  canCancel: boolean;
  canEdit: boolean;
  canCheckin: boolean;
  canSnooze: boolean;
  revision: number;
}

export interface ReservationItemMeetingWaitList {
  id: number;
  buildingDisplayName: string;
  floorName: string;
  status: string;
  spaceBookingId: number;
  canCancel: boolean;
  canEdit: boolean;
  name: string;
}

export interface ReservationItemCount {
  active: number;
  total: number;
}

export interface ReservationDetails extends Item {
  startDate: string;
  endDate: string;
  spaceBookings: BookingDetails[];
  virtualBookings: VirtualBookingDetails[];
  meetingWaitLists: MeetingWaitListDetails[];
  eventType: Item;
  teamRequest: {
    id: string;
    teamName: string;
  };
  vip: boolean;
  pointOfContact: User;
  series: Item;
  classification: string;
  actualAttendeesCount: number;
  estimatedAttendeesCount: number;
  business: Item;
  businessArea: Item;
  specialist: User;
  owner: User;
  status: string;
  statusReason: StatusReason;
  application: Item;
  allowedStatusActions: string[];
  canEdit: boolean;
  canCancel: boolean;
  type: string;
  revision: number;
  stopSeriesDate: string;
  sourceApplication: string;
  itemCount: {
    meetingWaitList: ReservationItemCount;
    virtualBooking: ReservationItemCount;
    spaceBooking: ReservationItemCount;
  };
}

export interface VirtualBookingDetails extends Item {
  referenceNumber: number;
  status: string;
  statusReason: StatusReason;
  comment: string;
  startDate: string;
  endDate: string;
  technologyDetails: TechnologyDetails;
  additionalDetailIds: number[];
  estimatedAttendeesCount: number;
  timeZoneIanaName: string;
  producer: User;
  facilitator: User;
  createDate: string;
  createBy: User;
  reservationId: number;
  flexibleDateAndTime: boolean;
  details: string;
  isRecurrent: boolean;
  canEdit: boolean;
  canCancel: boolean;
  hasAttendee: boolean;
  hasTask: boolean;
  hasAttachments: boolean;
  hasCommentsAlert: boolean;
  partOfHybridEvent: boolean;
  timeZoneAbbreviation: string;
  actualAttendeesCount: number;
  timeZoneName: string;
  revision: number;
  virtualBookingReferenceNumber: string;
  consultationRequested: boolean;
  consultationProvided: boolean;
}

export interface TechnologyDetails {
  connectMeetingId: string;
  technology: VirtualBookingTechnology;
  link: string;
  passCode: string;
  accountId: string;
  accountName: string;
}

export interface MeetingWaitListDetails extends Item {
  buildingId: number;
  buildingDisplayName: string;
  attendeesCount: number;
  hasAttendees: boolean;
  createDate: string;
  startDate: string;
  endDate: string;
  floorUniqueKey: number;
  floorName: string;
  spaceSetup: ItemWithDescription;
  isPrivate: boolean;
  isRecurrent: boolean;
  details: string;
  flexibleDateAndTime: boolean;
  hasCatering: boolean;
  cateringOrderId: number;
  canCancel: boolean;
  canEdit: boolean;
  revision: number;
  owner: User;
  meetingWaitListRequestType: string;
  reservationId: number;
  meetingWaitListStatusType: MeetingWaitlistStatus;
  statusReason: StatusReason;
  comment: string;
  fulfillmentDetails: {
    spaceBookingId: number;
    spaceName: string;
  };
  spaceFeatureIds: number[];
  eventRegisterStatus: string;
  partOfHybridEvent: boolean;
  timeZoneAbbreviation: string;
}

export enum VirtualBookingTechnology {
  VideoEnabled = 'VideoEnabled',
  Webinar = 'Webinar',
  LiveStreaming = 'LiveStreaming',
  MultiComplex = 'MultiComplex',
  WebExTeams = 'WebExTeams',
}

export enum MeetingWaitlistStatus {
  Requested = 'Requested',
  Pending = 'Pending',
  Confirmed = 'Confirmed',
  Unfulfilled = 'Unfulfilled',
  Cancelled = 'Cancelled',
}

export enum VirtualBookingStatus {
  Requested = 'Requested',
  Pending = 'Pending',
  Confirmed = 'Confirmed',
  Unfulfilled = 'Unfulfilled',
  Cancelled = 'Cancelled',
}

export interface EditReservationDetails {
  name: string;
  teamRequestId?: string;
  eventTypeId: number;
  ownerId: number;
  revision: number;
  vip: boolean;
  pointOfContactId?: number;
  seriesId?: number;
  actualAttendeesCount?: number;
  estimatedAttendeesCount?: number;
  businessId?: number;
  businessAreaId?: number;
  specialistId?: number;
  status: string;
  statusReasonId?: number;
  statusComment?: string;
  stopSeriesDate?: string;
  spaceBookingActualAttendeesCounts?: {
    spaceBookingId: number;
    actualAttendeesCount: number;
  }[];
  virtualBookingActualAttendeesCounts?: {
    virtualBookingId: string;
    actualAttendeesCount: number;
  }[];
}

export interface BulkEditInput {
  name: BulkEditField;
  specialistId: BulkEditField;
  setupTypeId: BulkEditField;
  attendeesCount: BulkEditField;
  startDate: BulkEditField;
  startTime: BulkEditField;
  endTime: BulkEditField;
  spaceBookings: IdWithRevision[];
}

export interface BulkEditField {
  value: string | number;
  specified: boolean;
}

export interface BulkChangeSpaceInput {
  spaceBookings: BulkChangeSpaceBooking[];
}

export interface BulkChangeSpaceBooking extends IdWithRevision {
  spaceId: number;
}

export interface BulkInput {
  reservationTargetType: ReservationTargetType;
  newReservationData?: BulkNewReservationInput;
  startDate?: string;
  existingReservationData?: {
    id: 0;
  };
  spaceBookings: Array<{ id: number }>;
  meetingWaitLists: Array<{ id: number }>;
}

export interface BulkCopyInput extends BulkInput {
  virtualBookings?: [{ id: string }];
  startDate: string;
}

export interface BulkNewReservationInput {
  name: string;
  eventTypeId: number;
  pointOfContactId: number;
  vip: boolean;
}

export enum ReservationItemType {
  MeetingRequest = 'MeetingRequest',
  VirtualBooking = 'VirtualBooking',
  SpaceBooking = 'SpaceBooking',
}

export enum ReservationTargetType {
  New = 'New',
  Existing = 'Existing',
}

export enum ReservationFeature {
  Undefined = 'Undefined',
  IncludeVirtualBookings = 'IncludeVirtualBookings',
  IncludeMeetingWaitLists = 'IncludeMeetingWaitLists',
  IncludeSpaceBookings = 'IncludeSpaceBookings',
  IncludeAllowedStatusActions = 'IncludeAllowedStatusActions',
  IncludeItemCount = 'IncludeItemCount',
}

export interface SpaceAvailabilityInput {
  spaceIds: number[];
  eventTime: EventTime;
  availabilityExcludeBookingIds: number[];
  features: string;
}

export interface SpaceAvailabilityResponseItem {
  space: SpaceView;
  isFavorite: boolean;
  inaccessibleToUser: boolean;
  rank: boolean;
  colleagues: SpaceAvailabilityUser[];
  availabilityType: string;
  timeSlots: TimeSlot[];
  suggestedAvailableTimeSlot: {
    startDate: string;
    endDate: string;
  };
  availableDays: string[];
  bookedTimeSlotMatches: TimeSlot[];
}

export interface SpaceAvailabilityUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface TimeSlot {
  id?: number;
  startDate?: string;
  endDate?: string;
  free: boolean;
  bookings: SpaceAvailabilityBooking[];
}

export interface SpaceAvailabilityBooking {
  startDate: string;
  endDate: string;
  id: number;
  setupTime: string;
  tearDownTime: string;
  user: SpaceAvailabilityUser;
}

export interface TeamRequestDashboardInput {
  term?: string;
  features?: DashboardFeature;
  teamRequestId?: string;
  startDate: string;
  endDate: string;
  orderType?: SortOrder;
  teamRequestItemDashboardSortingType?: TeamRequestDashboardSorting;
  spaceTypes?: string[];
  userIds?: number[];
  requestedByIds?: number[];
  buildingIds?: number[];
  limit?: number;
  page?: number;
}

export interface TeamRequestDashboardResponse {
  items: TeamRequestDashboardItem[];
  totalCount: number;
  facets: {
    requesters: User[];
  };
}

export interface TeamRequestDashboardItem {
  id: string;
  request: TeamRequest;
  user: User;
  requestedBy: User;
  createDate: string;
  spaceType: SpaceType;
  recurrenceRule: RecurrenceRule;
  businessId: number;
  buildingName: string;
  neighborhoodId: string;
}

export enum TeamRequestDashboardSorting {
  Created = 'Created',
  TeamName = 'TeamName',
  User = 'User',
  RequestedBy = 'RequestedBy',
  SpaceType = 'SpaceType',
  Date = 'Date',
  StartTime = 'StartTime',
  EndTime = 'EndTime',
}

export enum SpaceTypes {
  Workstation = 'Workstation',
  PrivateSpace = 'PrivateSpace',
  MeetingRoom = 'MeetingRoom',
  OpenSeating = 'OpenSeating',
}

export interface TeamRequest {
  id: number;
  teamName: string;
}

export interface TeamRequestDashboardValue {
  buildings: { id: number }[];
  owners: SelectedOwnerItem[];
  requestors: SelectedOwnerItem[];
  spaceTypes: string[];
  dates: Date[];
  term: string;
  property: TeamRequestDashboardSorting;
  order: SortOrder;
}

export interface TeamRequestPreferenceData {
  buildings?: { id: number }[];
  spaceTypes?: string[];
  form?: TeamRequestDashboardValue;
}

// MR dashboard In-Office
// Payload model
export interface InOfficeDashboardRequest {
  term: string;
  startDate: string;
  endDate: string;
  hasCatering?: boolean;
  meetingWaitListBuildingSearchCriterias?: {
    buildingId: number;
    meetingWaitListFloorSearchCriteria: { any: boolean; floorUniqueKeys: number[] };
  }[];
  meetingWaitListStatusTypes?: string[];
  meetingWaitListRequestTypes?: string[];
  eventTypeIds?: number[];
  reservationSpecialistSearchCriteria?: { specialistIds: number[]; notAssigned?: boolean };
  pointOfContactId?: number | null;
  meetingWaitListSorting?: { meetingWaitListSortingType: string; orderType: string }[];
  features?: DashboardFeature;
  limit?: number;
  page?: number;
}

// Response model

export interface MeetingWaitlistDashboard {
  items: MeetingWaitlist[];
  totalCount?: number;
  meetingWaitListDashboardFacets: { reservationSpecialists: User[] };
}

export interface MeetingWaitlist {
  id: number;
  name: string;
  owner: User;
  attendeesCount: number;
  hasAttendees: boolean;
  createDate: string;
  startDate: string;
  endDate: string;
  floorUniqueKey: number;
  floorName: string;
  eventType: EventType;
  spaceSetupType: SpaceSetupType;
  flexibleDateAndTime: boolean;
  cateringOrderId: number;
  contactMeLater: boolean;
  meetingWaitListRequestType: Item;
  meetingWaitListStatusType: Item;
  reservationId: number;
  partOfHybridEvent: boolean;
  vip: boolean;
  reservationSpecialist: User;
  pointOfContact: User;
  meetingWaitListRevision: number;
  reservationRevision: number;
  timeZoneAbbreviation: string;
  buildingName: string;

  // assigned in UI
  isCurrentDate?: boolean;
  reservationName?: string;
  specialistSearchOpen?: boolean;
  requestTypeChangeOpen?: boolean;
  reservationStatusType?:string;
}

// virtual dashboard

// payload model
export interface VirtualDashboardRequest {
  term?: string;
  startDate: string;
  endDate: string;
  status?: string[];
  features?: DashboardFeature;
  virtualBookingDashboardSortings?: { property: string; order: string }[]; // DashboardSorting[];
  eventTypeIds?: number[];
  hasCommentsAlerts?: boolean;
  hasAttachments?: boolean;
  hasTasks?: boolean;
  assignedToMe?: boolean;
  limit?: number;
  page?: number;
  reservationSpecialistSearchCriteria?: { specialistIds: number[]; notAssigned?: boolean };
  facilitatorSearchCriteria?: { facilitatorIds: number[]; notAssigned?: boolean };
  producerSearchCriteria?: { producerIds: number[]; notAssigned?: boolean };
  isConsultationRequested?: boolean;
}

// response model
export interface VirtualBookingsDashboard {
  items: VirtualBookings[];
  totalCount: number | null;
  virtualBookingFacets: VirtualBookingDashBoardFacet;

  reservationId(reservationId: any, id: number): unknown;
}

export interface VirtualBookings {
  reservationId: number;
  virtualBookingReferenceNumber: string;
  virtualBookingId: string;
  bookingName: string;
  startDate: string;
  endDate: string;
  statusType: string;
  partOfHybridEvent: boolean;
  reservationSpecialist?: User;
  pointOfContact: User;
  facilitator?: User;
  producer?: User;
  owner: User;
  eventTypeName: string;
  createDate: string;
  vip: boolean;
  timeZoneIanaName: string;
  flexibleDateAndTime: boolean;
  hasAttendees: boolean;
  hasTask: boolean;
  hasAttachments: boolean;
  hasCommentsAlert: boolean;
  reservationRevision: number;
  virtualBookingRevision: number;
  timeZoneAbbreviation: string;

  // assigned in UI
  isCurrentDate?: boolean;
  reservationName?: string;
  specialistSearchOpen?: boolean;
  producerSearchOpen?: boolean;
  facilitatorSearchOpen?: boolean;
  consultationRequested: boolean;
  consultationProvided: boolean;
  reservationStatusType?:string;
}

export interface VirtualBookingDashBoardFacet {
  reservationSpecialists: User[];
  producers: User[];
  facilitators: User[];
}

//details response
export interface CateringCount {
  taskCount: number;
  taskDueCount: number;
  commentCount: number;
  cateringCount: number;
  cateringDueCount: number;
  attachmentCount: number;
}

export interface InOfficeDetails {
  meetingWaitlist: MeetingWaitListDetails;
  reservation: ReservationDetails;
  cateringCount?: CateringCount;
}

export enum SpaceAvailabilityStatus {
  Match = 'Match',
  AvailableRestricted = 'AvailableRestricted',
  PartialMatch = 'PartialMatch',
  PartialMatchRestricted = 'PartialMatchRestricted',
}

export const SpaceAvailabilityStatusName = new Map<string, string>([
  [SpaceAvailabilityStatus[SpaceAvailabilityStatus.Match], 'Translation.Match'],
  [SpaceAvailabilityStatus[SpaceAvailabilityStatus.AvailableRestricted], 'Translation.Available_restricted'],
  [SpaceAvailabilityStatus[SpaceAvailabilityStatus.PartialMatch], 'Translation.Partial_match'],
  [SpaceAvailabilityStatus[SpaceAvailabilityStatus.PartialMatchRestricted], 'Translation.Partial_match_restricted'],
]);

export interface MRRecommendation {
  totalCount: number;
  items: MRRecommendationItem[];
}

export interface MRRecommendationItem {
  isRestricted: boolean;
  isManaged: boolean;
  startDate: string;
  endDate: string;
  floorName: string;
  floorUniqueKey: number;
  spaceId: number;
  spaceName: string;
  capacity: number;
  matchType: SpaceAvailabilityStatus;
  roomFeatures: number[];
  tearDownTime: string;
  spaceSetupTime: string;
  availabilityType: AvailabilityType;
  spaceType: SpaceType;
  timeRange: string;
}

export interface ConfirmMRModel {
  spaceId: number;
  singleDayEventData: SingleDayEventData;
  spaceBookingSpecialistId?: number;
  reservationSpecialistId?: {
    value: number;
    specified: boolean;
  };
}

export interface ConfirmMRResponse {
  reservationId: number;
  spaceBookingId: number;
}

export interface VirtualDetails {
  meetingWaitlist: VirtualBookingDetails;
  reservation: ReservationDetails;
  Count?: Count;
}

export interface VirtualRequestTaskRecords {
  items: VirtualAuditTaskDetails[];
  totalCount: number;
}

export interface VirtualAuditTaskDetails {
  taskId: string;
  createDateUTC: string;
  changeBy: string;
  changeDate: string;
  propertyName: string;
  oldValue: string;
  newValue: string;
  timeZoneId: string;
}

export interface Count {
  taskCount: number;
  taskDueCount: number;
  commentCount: number;
  cateringCount: number;
  cateringDueCount: number;
  attachmentCount: number;
}

export interface TimeZoneDropdownType {
  Id: string;
  Name: string;
}

export interface VirtualBookingTechnologyAccounts {
  id: string;
  name: string;
}

export interface VirtualBookingAdditionalDetails {
  id: number;
  description: string;
  technologies: string[];
}

export interface VirtualBookingModify {
  status: string;
  statusReasonId: number;
  statusComment: string;
  name: string;
  startDate: string;
  endDate: string;
  timeZoneIanaName: string;
  technologyDetails: TechnologyDetails;
  additionalDetailIds: number[];
  estimatedAttendeesCount: number;
  flexibleDateAndTime: boolean;
  producerId: number;
  facilitatorId: number;
  details: string;
  reservationId: number;
  revision: number;
  consultationProvided: boolean;
  consultationRequested: boolean;
}

export interface CancelFlags {
  needsReview?: boolean;
  hasCatering?: boolean;
  isLastItem: boolean;
  hasOtherServiceOrders?: boolean;
}

export interface MeetingWaitlistAuditItem {
  valueChange: string;
  oldValue: string;
  newValue: string;
  changedBy: string;
  changedDate: string;
}

export interface MeetingWaitListReleaseData {
  id: number;
  statusReasonId?: number;
  statusComment?: string;
  revision: number;
}

export interface MeetingWaitlistEditData {
  buildingId: number;
  name: string;
  attendeesCount: number;
  singleDayEventData: SingleDayEventData;
  floorUniqueKey: number;
  spaceSetupTypeId: number;
  isPrivate: boolean;
  details: string;
  flexibleDateAndTime: boolean;
  meetingWaitListRequestType: string;
  spaceFeatureIds: number[];
  revision: number;
  reservationId: number;
}
export class RecurrentEventData {
  RecurrenceRule: RecurrenceRule;
}

export interface VirtualBookingInput {
  name: string;
  eventTime: {
    timeSlotType: string;
    recurrentEventData?: RecurrentEventData;
    singleDayEventData?: {
      startDate: string;
      endDate: string;
    };
  };
  timeZoneIanaName: string;
  estimatedAttendeesCount: number;
  flexibleDateAndTime: boolean;
  status: string;
  technologyDetails: TechnologyDetails;
  aditionalDetailIds: number[];
  producerId: number;
  facilitatorId: number;
}

export interface MeetingWaitlistInput {
  buildingId: number;
  name: string;
  attendeesCount: number;
  floorUniqueKey: number;
  spaceSetupTypeId: number;
  isPrivate: boolean;
  flexibleDateAndTime: boolean;
  details: string;
  meetingWaitListRequestType: string;
  spaceFeatureIds: number[];
  eventTime: EventTime;
}

export interface MeetingRequestDashboardTabs extends IDynamicTab {
  status: string;
  time: number;
}
export enum MeetingsRequestTabs {
  VirtualDetails = 'Translation.Virtual_details',
  MeetingOverview = 'Translation.Meeting_overview',
  ReservationOverview = 'Translation.Reservationoverview',
  LTROverview = 'Translation.LTRoverview',
  Catering = 'Translation.Catering',
  Comments = 'Translation.Comments',
  Expenses = 'Translation.Expenses',
  Attendees = 'Translation.Attendees',
  AuditTrail = 'Translation.Audit_trail',
  Documents = 'Translation.Documents',
  Tasks = 'Translation.Tasks',
  RequestDetails = 'Translation.Request_details',
  Recommendation = 'Translation.Recommendation',
  EditTask = 'editTask',
  AddTask = 'addTask',
}

export interface VirtualOptions{
  isCheckBox?:boolean ;
  category:string;
  categoryId:number;
  additionalDetails:MultiSelectItem[];
}
export interface ConfirmMeetingWaitlistAdditionalData {
  bookingSpecialist: SelectedOwnerItem;
  meetingSpecialist: SelectedOwnerItem;
}
